import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Briefcase, Plus, ExternalLink, Check, Clock } from "lucide-react";
import { PRIORITIES } from "@/lib/crm";

function AreaChip({ area, onPropose }) {
  const s = area.status;
  if (s === "posseduto")
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`area-owned-${area.key}`}>
        <Check className="h-3 w-3" /> {area.label}
      </span>
    );
  if (s === "in_corso")
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`area-inprogress-${area.key}`}>
        <Clock className="h-3 w-3" /> {area.label} · in corso
      </span>
    );
  return (
    <button
      onClick={onPropose}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-brand hover:bg-brand/5 hover:text-brand"
      data-testid={`area-missing-${area.key}`}
      title={`Proponi ${area.label}`}
    >
      <Plus className="h-3 w-3" /> {area.label}
    </button>
  );
}

export default function PortfolioPage() {
  const { user } = useAuth();
  const crossOffice = user?.role === "admin" || user?.role === "backoffice";
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, areas: [] });
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [areaDefs, setAreaDefs] = useState([]);
  const [search, setSearch] = useState("");
  const [fOffice, setFOffice] = useState("");
  const [fOperator, setFOperator] = useState("");
  const [fHas, setFHas] = useState("");
  const [fMissing, setFMissing] = useState("");
  const [propose, setPropose] = useState(null); // { customer, area }

  useEffect(() => {
    api.get("/portfolio/areas").then(({ data }) => setAreaDefs(data)).catch(() => {});
    if (crossOffice) api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [crossOffice, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 50 };
      if (search) params.search = search;
      if (fOffice) params.office_id = fOffice;
      if (fOperator) params.operator_id = fOperator;
      if (fHas) params.has_product = fHas;
      if (fMissing) params.missing_product = fMissing;
      const { data } = await api.get("/portfolio", { params });
      setData(data);
    } catch (e) {
      toast.error("Impossibile caricare il portafoglio.");
    } finally { setLoading(false); }
  }, [search, fOffice, fOperator, fHas, fMissing]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="portfolio-page">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><Briefcase className="h-6 w-6 text-brand" /> Portafoglio Commerciale</h1>
        <p className="mt-1 text-sm text-slate-500">Cross-selling sui clienti esistenti. Verde = già posseduto · giallo = opportunità in corso · grigio = proponibile.</p>
      </div>

      <div className="ds-card flex flex-wrap items-end gap-3 p-4" data-testid="portfolio-filters">
        <div className="space-y-1"><Label className="ds-label">Cliente</Label><Input placeholder="Cerca cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-[190px]" data-testid="portfolio-search" /></div>
        {crossOffice && (
          <div className="space-y-1"><Label className="ds-label">Sede</Label>
            <Select value={fOffice || "all"} onValueChange={(v) => setFOffice(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="portfolio-office-filter"><SelectValue placeholder="Sede" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Tutte</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {isAdmin && (
          <div className="space-y-1"><Label className="ds-label">Operatore</Label>
            <Select value={fOperator || "all"} onValueChange={(v) => setFOperator(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="portfolio-operator-filter"><SelectValue placeholder="Operatore" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1"><Label className="ds-label">Possiede</Label>
          <Select value={fHas || "all"} onValueChange={(v) => setFHas(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="portfolio-has-filter"><SelectValue placeholder="Prodotto" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Qualsiasi</SelectItem>{areaDefs.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="ds-label">Manca</Label>
          <Select value={fMissing || "all"} onValueChange={(v) => setFMissing(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="portfolio-missing-filter"><SelectValue placeholder="Prodotto" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Qualsiasi</SelectItem>{areaDefs.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        : data.items.length === 0 ? <div className="ds-card py-12 text-center text-sm text-slate-500" data-testid="portfolio-empty">Nessun cliente trovato con i filtri correnti.</div>
        : (
          <>
            <div className="text-xs text-slate-500" data-testid="portfolio-total">{data.total} clienti nel portafoglio</div>
            <div className="ds-card ds-table-scroll p-0" data-testid="portfolio-table">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="px-4 py-3">Cliente</th><th className="hidden px-4 py-3 md:table-cell">Sede</th><th className="px-4 py-3">Portafoglio prodotti</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((c) => (
                    <tr key={c.customer_id} className="hover:bg-brand/5" data-testid={`portfolio-row-${c.customer_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{c.customer_name}</div>
                        <div className="text-[11px] text-slate-400">{c.type}{c.operator_name ? ` · ${c.operator_name}` : ""}</div>
                      </td>
                      <td className="hidden px-4 py-3 text-slate-600 md:table-cell">{c.office_name || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {c.areas.map((a) => <AreaChip key={a.key} area={a} onPropose={() => setPropose({ customer: c, area: a })} />)}
                        </div>
                      </td>
                      <td className="px-4 py-3"><Button size="icon" variant="ghost" title="Apri cliente" onClick={() => navigate(`/clienti/${c.customer_id}`)} data-testid={`portfolio-open-${c.customer_id}`}><ExternalLink className="h-4 w-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

      {propose && <ProposeDialog data={propose} onClose={() => setPropose(null)} onSaved={() => { setPropose(null); load(); }} />}
    </div>
  );
}

function ProposeDialog({ data, onClose, onSaved }) {
  const { customer, area } = data;
  const [priority, setPriority] = useState("Media");
  const [nextAction, setNextAction] = useState(`Contattare per proposta ${area.label}`);
  const [nextDate, setNextDate] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!nextAction.trim() || !nextDate) { setError("Indica la prossima azione e la data."); return; }
    setSaving(true); setError("");
    try {
      await api.post("/portfolio/opportunity", {
        customer_id: customer.customer_id, area: area.key, priority,
        next_action: nextAction, next_action_date: nextDate,
        estimated_value: value === "" ? null : Number(value), notes: notes || null,
      });
      toast.success("Opportunità creata dal portafoglio");
      onSaved();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-md" data-testid="portfolio-propose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Briefcase className="h-5 w-5 text-brand" /> Nuova opportunità · {area.label}</DialogTitle>
          <DialogDescription>Cross-selling per <strong>{customer.customer_name}</strong>. Verrà creata un'opportunità collegata al cliente nel normale workflow.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="portfolio-propose-error">{error}</div>}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Priorità</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="portfolio-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Valore stimato (€)</Label><Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} data-testid="portfolio-value" placeholder="Opzionale" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-brand">Prossima azione *</Label><Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} data-testid="portfolio-next-action" /></div>
          <div className="space-y-1.5"><Label className="text-brand">Data prossima azione *</Label><Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="portfolio-next-date" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="portfolio-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="portfolio-create-opp">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea opportunità"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
